/**
 * Subtitle source URL construction from Jellyfin DeliveryUrl.
 * Uses the server-provided DeliveryUrl (which has the correct
 * concrete mediaSourceId) rather than building URLs from scratch.
 */
import type { Credentials } from '../storage/credentials';
import type { MediaStream } from './presentation';
import {
  getSubtitleDeliveryFormat,
  getSubtitleRendererKind,
  normalizeSubtitleFormat,
  type SubtitleDeliveryFormat,
  type SubtitleRendererKind,
  type SubtitleHandling,
} from './subtitleFormat';

// ---- public types ----

export type SubtitleSourceMode = 'webvtt' | 'json-events' | 'binary';

export interface SubtitleSourceCandidate {
  url: string;
  mode: SubtitleSourceMode;
  source: 'delivery-url' | 'derived-delivery-url' | 'external-url' | 'generated-fallback';
  routeStartTicks: number;
}

export interface SubtitleTrackDescriptor {
  streamIndex: number;
  src: string;
  srcCandidates: SubtitleSourceCandidate[];
  srclang: string;
  label: string;
  default: boolean;
  format: string;
  deliveryFormat: SubtitleDeliveryFormat | null;
  handling: SubtitleHandling;
  rendererKind: SubtitleRendererKind;
  isExternal: boolean;
  routeStartTicks: number;
  sourceKey: string;
}

/** File hint for bitmap renderers whose URL query hides the real extension. */
export function getSubtitleCandidateFileName(url: string): string | undefined {
  try {
    const pathname = new URL(url, 'https://jellyflix.invalid').pathname;
    const fileName = pathname.split('/').pop();
    return fileName ? decodeURIComponent(fileName) : undefined;
  } catch {
    const path = url.split(/[?#]/, 1)[0];
    const fileName = path.split('/').pop();
    return fileName || undefined;
  }
}

// ---- internal helpers ----

function getServerUrl(credentials: Credentials): string {
  return credentials.serverUrl.replace(/\/$/, '');
}

/**
 * Given a DeliveryUrl like:
 *   /Videos/{itemId}/{concreteId}/Subtitles/{index}/{ticks}/Stream.sup?ApiKey=XX
 * Return the same URL with a different format extension substituted.
 */
function extractConcreteId(transcodingUrl: string): string {
  try {
    // Remux puts the concrete mediaSourceId in the URL query string:
    // /videos/{itemId}/master.m3u8?MediaSourceId={concreteId}&...
    const url = new URL(transcodingUrl, 'https://x');
    const msid = url.searchParams.get('MediaSourceId');
    if (msid) return msid;
    // Fallback: try path (for non-Remux Jellyfin)
    const parts = url.pathname.split('/');
    const vidIdx = parts.indexOf('videos');
    if (vidIdx >= 0 && parts.length > vidIdx + 2) return parts[vidIdx + 2];
  } catch {}
  return '';
}

function substituteFormat(
  deliveryUrl: string,
  targetFormat: string,
  server: string,
  token: string,
): string {
  try {
    // Build absolute URL first
    const base = deliveryUrl.startsWith('http://') || deliveryUrl.startsWith('https://')
      ? deliveryUrl
      : `${server}${deliveryUrl.startsWith('/') ? '' : '/'}${deliveryUrl}`;

    const url = new URL(base);
    const pathname = url.pathname;
    const parts = pathname.split('/');
    if (parts.length < 2) return base;

    const filename = parts[parts.length - 1];
    const dotIdx = filename.lastIndexOf('.');
    if (dotIdx < 0) return base;

    const baseName = filename.slice(0, dotIdx);
    parts[parts.length - 1] = `${baseName}.${targetFormat}`;

    // Rebuild clean URL with only our ApiKey
    const cleanPath = parts.join('/');
    return `${url.origin}${cleanPath}?ApiKey=${encodeURIComponent(token)}`;
  } catch {
    return deliveryUrl;
  }
}

/**
 * Extract format from a DeliveryUrl path (e.g. "sup" from Stream.sup).
 */
function getFormatFromUrl(url: string): string {
  try {
    const pathname = url.includes('://')
      ? new URL(url).pathname
      : url.split('?')[0];
    const filename = pathname.split('/').pop() || '';
    const dotIdx = filename.lastIndexOf('.');
    return dotIdx >= 0 ? filename.slice(dotIdx + 1).toLowerCase() : '';
  } catch {
    return '';
  }
}

function getRouteStartTicks(deliveryUrl: string): number {
  try {
    const pathname = deliveryUrl.includes('://')
      ? new URL(deliveryUrl).pathname
      : deliveryUrl.split('?')[0];
    const parts = pathname.split('/');
    const subIdx = parts.indexOf('Subtitles');
    if (subIdx >= 0 && subIdx + 2 < parts.length) {
      const ticks = parseInt(parts[subIdx + 2], 10);
      if (Number.isFinite(ticks)) return ticks;
    }
  } catch { /* ignore */ }
  return 0;
}

// ---- main exports ----

export function buildSubtitleTracks(
  credentials: Credentials,
  itemId: string,
  mediaSourceId: string,
  subtitleStreams: MediaStream[],
  transcodingUrl?: string | null,
): SubtitleTrackDescriptor[] {
  const server = getServerUrl(credentials);
  const token = credentials.accessToken;
  const currentMediaSourceId = (transcodingUrl && extractConcreteId(transcodingUrl)) || mediaSourceId;

  return subtitleStreams.map((stream) => {
    const codec = stream.Codec ?? '';
    const streamIndex = stream.Index ?? 0;
    // Server misreports codecs as "srt" — use the real format from
    // the DeliveryUrl filename (e.g. Stream.ass, Stream.sup).
    const urlFormat = getFormatFromUrl(stream.DeliveryUrl ?? '');
    const detectedCodec = urlFormat && urlFormat !== 'vtt' && urlFormat !== 'srt'
      ? urlFormat
      : codec;
    const rendererKind = getSubtitleRendererKind(detectedCodec);

    const deliveryUrl = stream.DeliveryUrl;
    const candidates: SubtitleSourceCandidate[] = [];

    if (deliveryUrl) {
      // Substitute the stale concrete mediaSourceId from the item
      // metadata with the current session's ID from the active
      // TranscodingUrl.  Without this, the subtitle endpoint times
      // out because the concrete ID has expired.
      let effectiveUrl = deliveryUrl;
      if (transcodingUrl) {
        const currentId = extractConcreteId(transcodingUrl);
        if (currentId) {
          effectiveUrl = deliveryUrl.replace(
            /\/Videos\/[^/]+\/[^/]+\//,
            `/Videos/${encodeURIComponent(itemId)}/${encodeURIComponent(currentId)}/`
          );
        }
      }
      const routeStartTicks = getRouteStartTicks(deliveryUrl);
      const urlFormat = getFormatFromUrl(deliveryUrl);

      if (rendererKind === 'jassub') {
        // ASS/SSA → request raw ASS binary
        candidates.push({
          url: substituteFormat(effectiveUrl, 'ass', server, token),
          mode: 'binary',
          source: 'delivery-url',
          routeStartTicks,
        });
      } else if (rendererKind === 'libbitsub') {
        // PGS/bitmap → use delivery URL directly as binary
        candidates.push({
          url: substituteFormat(effectiveUrl, urlFormat || 'sup', server, token),
          mode: 'binary',
          source: 'delivery-url',
          routeStartTicks,
        });
        // Also try VTT fallback for text-based rendering
        candidates.push({
          url: substituteFormat(effectiveUrl, 'vtt', server, token),
          mode: 'webvtt',
          source: 'derived-delivery-url',
          routeStartTicks,
        });
      } else {
        // Text-based → request VTT
        candidates.push({
          url: substituteFormat(effectiveUrl, 'vtt', server, token),
          mode: 'webvtt',
          source: 'delivery-url',
          routeStartTicks,
        });
      }
    }

    // Generated fallback if no delivery URL
    if (candidates.length === 0 && itemId && currentMediaSourceId) {
      const fallbackFormat = rendererKind === 'jassub'
        ? 'ass' : rendererKind === 'libbitsub' ? 'sup' : 'vtt';
      const fallbackUrl = `${server}/Videos/${encodeURIComponent(itemId)}/${encodeURIComponent(currentMediaSourceId)}/Subtitles/${streamIndex}/0/Stream.${fallbackFormat}?ApiKey=${encodeURIComponent(token)}`;
      candidates.push({
        url: fallbackUrl,
        mode: rendererKind ? 'binary' : 'webvtt',
        source: 'generated-fallback',
        routeStartTicks: 0,
      });
    }

    const handling: SubtitleHandling = rendererKind
      ? rendererKind === 'jassub'
        ? 'ass-render'
        : 'pgs-render'
      : candidates.length > 0
        ? 'external-vtt'
        : 'unavailable';

    return {
      streamIndex,
      src: candidates[0]?.url ?? '',
      srcCandidates: candidates,
      srclang: stream.Language ?? 'und',
      label: stream.DisplayTitle ?? stream.Language ?? `Track ${streamIndex}`,
      default: stream.IsDefault ?? false,
      format: codec,
      deliveryFormat: getSubtitleDeliveryFormat(codec),
      handling,
      rendererKind,
      isExternal: stream.IsExternal ?? false,
      routeStartTicks: candidates[0]?.routeStartTicks ?? 0,
      sourceKey: `${itemId}/${mediaSourceId}/${streamIndex}`,
    };
  });
}
